/**
 * Visual experiments configuration: sources, normalization and cache
 * (contract §2).
 *
 * Precedence (first non-empty wins, no merging):
 *   1. inline  — b2mInit({ visualExperiments: [...] }) / window.b2mConfig.visualExperiments
 *   2. remote  — experiments-api, one anonymous snapshot per experiment id the
 *                site lists in `visualExperimentIds`:
 *                GET {experiments-api}/experiments/{experimentId}/visual-configuration
 *                → { editorUrl, urlTargets, variants: [{ groupId, elements }], settings }
 *   3. nothing — runtime idle
 *
 * Standalone module (no imports) so the unit tests can load it directly.
 */

export const VISUAL_CACHE_KEY = 'b2m_visual_config';
export const VISUAL_CACHE_TTL_MS = 5 * 60 * 1000;
export const EXPERIMENT_ID_PLACEHOLDER = '{experimentId}';
export const VISUAL_CONFIG_PATH = `/experiments/${EXPERIMENT_ID_PLACEHOLDER}/visual-configuration`;
export const DEFAULT_EXPERIMENTS_API_ORIGIN = 'https://experiments-api.b2metric.com';
export const DEFAULT_VISUAL_CONFIG_URL = `${DEFAULT_EXPERIMENTS_API_ORIGIN}${VISUAL_CONFIG_PATH}`;
export const MAX_VISUAL_EXPERIMENT_IDS = 50;
export const DEFAULT_EDITOR_ORIGINS = ['https://analytics.b2metric.com', 'https://analytics-test.b2metric.com', 'https://iqbeta.b2metric.com', 'https://iqbeta-test.b2metric.com'];
export const ACTIONS = ['text', 'style', 'hide', 'image', 'attribute', 'insert'];
export const INSERT_POSITIONS = ['beforebegin', 'afterbegin', 'beforeend', 'afterend', 'replace'];
export const INSERT_CSS_MAX_LENGTH = 10000;
export const URL_TARGET_TYPES = ['simple', 'regex', 'exact', 'contains'];
export const DEFAULT_SETTINGS = {
  antiFlicker: { mode: 'selectors', timeoutMs: 1200 },
  waitForElementMs: 5000
};

const lower = v => (v == null ? '' : String(v).trim().toLowerCase());

function normalizeChange(raw, index) {
  if (!raw || typeof raw !== 'object') return null;
  const action = lower(raw.action);
  if (!ACTIONS.includes(action)) return null;
  const selector = raw.selector == null ? '' : String(raw.selector).trim();
  if (!selector) return null;
  const change = {
    id: raw.id ? String(raw.id) : `c_${index + 1}`,
    action,
    selector,
    enabled: raw.enabled !== false,
    matchAll: raw.matchAll === true
  };
  if (raw.selectorMeta && typeof raw.selectorMeta === 'object') change.selectorMeta = raw.selectorMeta;
  if (action === 'attribute') {
    change.name = raw.name == null ? '' : String(raw.name).trim().toLowerCase();
    if (!change.name) return null;
    change.value = raw.value == null ? null : String(raw.value);
  } else if (action === 'style') {
    if (!raw.value || typeof raw.value !== 'object') return null;
    change.value = raw.value;
  } else if (action === 'image') {
    const v = raw.value && typeof raw.value === 'object' ? raw.value : { src: raw.value };
    if (!v.src) return null;
    change.value = { src: String(v.src), alt: v.alt == null ? undefined : String(v.alt) };
  } else if (action === 'text') {
    change.value = raw.value == null ? '' : String(raw.value);
  } else if (action === 'insert') {
    // HTML is sanitized by the applier at insert time.
    change.value = raw.value == null ? '' : String(raw.value);
    if (!change.value.trim()) return null;
    const position = lower(raw.position);
    change.position = INSERT_POSITIONS.includes(position) ? position : 'afterend';
    // Optional scoped CSS shipped with the HTML (sanitized by the applier);
    // over the limit it is dropped, the HTML still applies. `element` / `meta`
    // are panel-only form state and are not needed at runtime.
    if (typeof raw.css === 'string' && raw.css.trim() && raw.css.length <= INSERT_CSS_MAX_LENGTH) change.css = raw.css;
  }
  return change;
}

function normalizeSettings(raw) {
  const s = raw && typeof raw === 'object' ? raw : {};
  const af = s.antiFlicker && typeof s.antiFlicker === 'object' ? s.antiFlicker : {};
  const timeoutMs = Number(af.timeoutMs);
  const waitMs = Number(s.waitForElementMs);
  return {
    antiFlicker: {
      mode: af.mode === 'off' ? 'off' : 'selectors',
      timeoutMs: Number.isFinite(timeoutMs) && timeoutMs >= 0 ? timeoutMs : DEFAULT_SETTINGS.antiFlicker.timeoutMs
    },
    waitForElementMs: Number.isFinite(waitMs) && waitMs >= 0 ? waitMs : DEFAULT_SETTINGS.waitForElementMs
  };
}

/**
 * Validate and normalize one changeset. Returns null when it cannot be used
 * (no experiment id, no variants). Group/experiment ids are lower-cased so
 * they compare equal to the assign response regardless of GUID casing.
 * experiments-api snapshots name a variant's changes `elements` and carry no
 * experiment id (it is in the URL): pass it as `fallbackExperimentId`.
 */
export function normalizeChangeset(raw, fallbackExperimentId) {
  if (!raw || typeof raw !== 'object') return null;
  const experimentId = lower(raw.experimentId) || lower(fallbackExperimentId);
  if (!experimentId) return null;
  const variants = (Array.isArray(raw.variants) ? raw.variants : [])
    .map(v => {
      if (!v || typeof v !== 'object') return null;
      const groupId = lower(v.groupId);
      if (!groupId) return null;
      let list = [];
      if (Array.isArray(v.changes)) list = v.changes;
      else if (Array.isArray(v.elements)) list = v.elements;
      const changes = list
        .map(normalizeChange)
        .filter(Boolean);
      const out = { groupId, changes };
      if (typeof v.css === 'string' && v.css.trim()) out.css = v.css;
      if (v.redirect && typeof v.redirect === 'object' && v.redirect.url) out.redirect = v.redirect;
      return out;
    })
    .filter(Boolean);
  if (variants.length === 0) return null;
  const urlTargets = (Array.isArray(raw.urlTargets) ? raw.urlTargets : [])
    .filter(t => t && t.pattern != null && String(t.pattern).trim() !== '')
    .map(t => ({
      include: t.include !== false,
      type: URL_TARGET_TYPES.includes(lower(t.type)) ? lower(t.type) : 'simple',
      pattern: String(t.pattern).trim()
    }));
  return {
    schemaVersion: Number(raw.schemaVersion) || 1,
    experimentId,
    editorUrl: raw.editorUrl ? String(raw.editorUrl) : '',
    urlTargets,
    variants,
    settings: normalizeSettings(raw.settings),
    updatedAt: raw.updatedAt ? String(raw.updatedAt) : undefined
  };
}

/** Normalize a list, dropping invalid entries and duplicate experiment ids. */
export function normalizeExperiments(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  list.forEach(raw => {
    const cs = normalizeChangeset(raw);
    if (cs && !seen.has(cs.experimentId)) {
      seen.add(cs.experimentId);
      out.push(cs);
    }
  });
  return out;
}

/**
 * Resolve the inline source.
 *   false / "false"    → false (kill switch)
 *   array / JSON array → normalized list (may be empty)
 *   anything else      → null (not provided)
 */
export function resolveInlineExperiments(value) {
  if (value === false || (typeof value === 'string' && value.trim().toLowerCase() === 'false')) return false;
  if (Array.isArray(value)) return normalizeExperiments(value);
  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      return normalizeExperiments(JSON.parse(value));
    } catch (e) {
      return null;
    }
  }
  if (value && typeof value === 'object' && Array.isArray(value.experiments)) {
    return normalizeExperiments(value.experiments);
  }
  return null;
}

/** Comma-separated string or array → list of normalized origins. */
export function parseOrigins(value) {
  const list = Array.isArray(value)
    ? value
    : (typeof value === 'string' ? value.split(',') : []);
  const out = [];
  list.forEach(v => {
    let s = String(v || '').trim().toLowerCase();
    if (!s) return;
    if (s.endsWith('/')) s = s.slice(0, -1);
    if (!/^https?:\/\//.test(s)) return;
    if (!out.includes(s)) out.push(s);
  });
  return out;
}

/** `visualExperimentIds`: array or comma/space separated string → unique lower-cased ids. */
export function parseExperimentIds(value) {
  let list = [];
  if (Array.isArray(value)) list = value;
  else if (typeof value === 'string') list = value.split(/[\s,]+/);
  const out = [];
  list.forEach(v => {
    const id = lower(v);
    if (id && /^[a-z0-9-]+$/.test(id) && !out.includes(id) && out.length < MAX_VISUAL_EXPERIMENT_IDS) out.push(id);
  });
  return out;
}

/**
 * Snapshot URL template (with `{experimentId}`):
 *   - `configUrl` set → used as is when it has the placeholder, otherwise it
 *     is the experiments-api base URL;
 *   - otherwise the origin of `experimentApiUrl` (the assign endpoint), so a
 *     site pointed at the test experiments-api reads its test configs.
 */
export function resolveConfigUrl({ configUrl, experimentApiUrl } = {}) {
  const explicit = configUrl == null ? '' : String(configUrl).trim();
  if (explicit) {
    if (explicit.includes(EXPERIMENT_ID_PLACEHOLDER)) return explicit;
    return `${explicit.replace(/\/+$/, '')}${VISUAL_CONFIG_PATH}`;
  }
  try {
    const { origin } = new URL(String(experimentApiUrl || ''));
    if (/^https?:/.test(origin)) return `${origin}${VISUAL_CONFIG_PATH}`;
  } catch (e) { /* default below */ }
  return DEFAULT_VISUAL_CONFIG_URL;
}

export function buildConfigUrl(template, experimentId) {
  return String(template || DEFAULT_VISUAL_CONFIG_URL).split(EXPERIMENT_ID_PLACEHOLDER).join(encodeURIComponent(experimentId));
}

/** Stable key of an id list (cache scope). */
export function experimentIdsKey(ids) {
  return (Array.isArray(ids) ? ids.slice() : []).sort().join(',');
}

function readRaw(storage, win) {
  try {
    if (storage && typeof storage.get === 'function') return storage.get(VISUAL_CACHE_KEY, null);
    const w = win || (typeof window !== 'undefined' ? window : null);
    if (!w || !w.localStorage) return null;
    const v = w.localStorage.getItem(VISUAL_CACHE_KEY);
    return v ? JSON.parse(v) : null;
  } catch (e) {
    return null;
  }
}

/** Cached remote config `{ experiments, fetchedAt, apiKey, ids }` or null. */
export function readCachedConfig(storage, win) {
  const raw = readRaw(storage, win);
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.experiments)) return null;
  return {
    experiments: normalizeExperiments(raw.experiments),
    fetchedAt: Number(raw.fetchedAt) || 0,
    apiKey: raw.apiKey || null,
    ids: typeof raw.ids === 'string' ? raw.ids : null
  };
}

export function isCacheFresh(cache, now = Date.now(), ttl = VISUAL_CACHE_TTL_MS) {
  return !!(cache && cache.fetchedAt && (now - cache.fetchedAt) <= ttl);
}

export function writeCachedConfig(storage, { experiments, apiKey, ids }, win) {
  const value = { experiments: experiments || [], fetchedAt: Date.now(), apiKey: apiKey || null, ids: ids == null ? null : String(ids) };
  try {
    if (storage && typeof storage.set === 'function') return storage.set(VISUAL_CACHE_KEY, value);
    const w = win || (typeof window !== 'undefined' ? window : null);
    if (w && w.localStorage) w.localStorage.setItem(VISUAL_CACHE_KEY, JSON.stringify(value));
    return true;
  } catch (e) {
    return false;
  }
}

async function fetchOne(f, url, timeoutMs) {
  const opts = { method: 'GET', headers: { Accept: 'application/json' }, credentials: 'omit' };
  let timer = null;
  if (typeof AbortController === 'function' && timeoutMs > 0) {
    const controller = new AbortController();
    opts.signal = controller.signal;
    timer = setTimeout(() => controller.abort(), timeoutMs);
  }
  try {
    return await f(url, opts);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Fetch the experiments-api snapshot of every id, in parallel. Never throws;
 * one result per id:
 *   { experimentId, status: 'ok', changeset } — usable configuration
 *   { experimentId, status: 'none' }          — 404 or nothing usable in it
 *   { experimentId, status: 'error' }         — network / HTTP error (keep the cache)
 */
export async function fetchVisualConfigurations({ url, experimentIds, fetchImpl, timeoutMs = 4000 }) {
  const f = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  const ids = parseExperimentIds(experimentIds);
  return Promise.all(ids.map(async experimentId => {
    if (!f) return { experimentId, status: 'error' };
    try {
      const res = await fetchOne(f, buildConfigUrl(url, experimentId), timeoutMs);
      if (res && res.status === 404) return { experimentId, status: 'none' };
      if (!res || !res.ok) return { experimentId, status: 'error' };
      const changeset = normalizeChangeset(await res.json(), experimentId);
      // The URL names the experiment: a snapshot never moves to another id.
      if (!changeset || changeset.experimentId !== experimentId) return { experimentId, status: 'none' };
      return { experimentId, status: 'ok', changeset };
    } catch (e) {
      return { experimentId, status: 'error' };
    }
  }));
}
